import { useEffect, useMemo, useRef } from 'react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { iaRa } from 'lib/helpers/utility';

const PRODUCT_NDC_FIELD = 'product_ndc';

export const MEDICATION_PRODUCT_NDC_GROUPING_PROPS = {
  groupDefaultExpanded: 0,
  groupDisplayType: 'custom',
  groupRemoveSingleChildren: true
};

const getPrimaryGroupedField = (colDefs = []) => {
  const primaryGroupedColumn =
    iaRa(colDefs).find((col) => col.rowGroupIndex === 0) ??
    iaRa(colDefs).find((col) => col.rowGroup);

  return primaryGroupedColumn?.field ?? null;
};

const hasDuplicateMedicationProductNdc = (medications = []) => {
  const seen = new Set();

  for (const medication of iaRa(medications)) {
    const productNdc =
      medication?.product_ndc == null || medication?.product_ndc === ''
        ? null
        : String(medication.product_ndc).trim();

    if (!productNdc) {
      continue;
    }

    if (seen.has(productNdc)) {
      return true;
    }

    seen.add(productNdc);
  }

  return false;
};

const enableProductNdcGrouping = (colDefs = []) => {
  return iaRa(colDefs).map((col) => {
    if (col.field !== PRODUCT_NDC_FIELD) {
      return col;
    }

    return {
      ...col,
      rowGroup: true,
      rowGroupIndex: 0
    };
  });
};

const disableProductNdcGrouping = (colDefs = []) => {
  return iaRa(colDefs).map((col) => {
    if (col.field !== PRODUCT_NDC_FIELD) {
      return col;
    }

    const { rowGroup: _rowGroup, rowGroupIndex: _rowGroupIndex, ...rest } = col;
    return rest;
  });
};

export const useMedicationProductNdcGrouping = ({ medications = [] } = {}) => {
  const { columnsReady, colDefs, gridApi, setColDefs } = useTableContext();
  const hasSyncedGridGrouping = useRef(false);
  const hasDuplicateProductNdc = useMemo(() => {
    return hasDuplicateMedicationProductNdc(medications);
  }, [medications]);

  useEffect(() => {
    const clearGrouping = () => {
      if (getPrimaryGroupedField(colDefs) === PRODUCT_NDC_FIELD) {
        setColDefs((prevColDefs) => disableProductNdcGrouping(prevColDefs));
      }

      if (gridApi?.applyColumnState) {
        gridApi.applyColumnState({
          state: [{ colId: PRODUCT_NDC_FIELD, rowGroup: false, rowGroupIndex: null }]
        });
      }

      hasSyncedGridGrouping.current = false;
    };

    if (!columnsReady || !iaRa(colDefs).length || !hasDuplicateProductNdc) {
      clearGrouping();
      return;
    }

    const hasProductNdcColumn = iaRa(colDefs).some((col) => col.field === PRODUCT_NDC_FIELD);

    if (!hasProductNdcColumn) {
      clearGrouping();
      return;
    }

    const primaryGroupedField = getPrimaryGroupedField(colDefs);

    if (primaryGroupedField && primaryGroupedField !== PRODUCT_NDC_FIELD) {
      clearGrouping();
      return;
    }

    if (primaryGroupedField !== PRODUCT_NDC_FIELD) {
      setColDefs((prevColDefs) => enableProductNdcGrouping(prevColDefs));
    }

    if (!gridApi?.applyColumnState) {
      hasSyncedGridGrouping.current = false;
      return;
    }

    const productNdcColumnState = gridApi.getColumnState?.().find((col) => {
      return col.colId === PRODUCT_NDC_FIELD;
    });
    const isGridAlreadyGroupedByProductNdc =
      productNdcColumnState?.rowGroup === true && productNdcColumnState?.rowGroupIndex === 0;

    if (isGridAlreadyGroupedByProductNdc && hasSyncedGridGrouping.current) {
      return;
    }

    gridApi.applyColumnState({
      defaultState: { rowGroup: false, rowGroupIndex: null },
      state: [{ colId: PRODUCT_NDC_FIELD, rowGroup: true, rowGroupIndex: 0 }]
    });
    hasSyncedGridGrouping.current = true;
  }, [colDefs, columnsReady, gridApi, hasDuplicateProductNdc, setColDefs]);
};
